'use client';

import * as Swetrix from 'swetrix';

export type AnalyticsEventName = string;

export interface AnalyticsEventData {
  [key: string]: string | number | boolean | null;
}

function shouldTrack(): boolean {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV === 'development') return false;
  
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return false;
  }
  
  return true;
}

/**
 * Track custom event via Swetrix
 */
export const trackEvent = (
  eventName: AnalyticsEventName,
  eventData?: AnalyticsEventData
): void => {
  if (!shouldTrack()) return;
  try {
    Swetrix.track({
      ev: eventName,
      ...(eventData ? { meta: eventData } : {}),
    });
  } catch (e) {
    // Ignore analytics errors silently
  }
};

export const trackPageView = (): void => {
  if (!shouldTrack()) return;
  try {
    Swetrix.trackViews();
  } catch (e) {
    // Ignore analytics errors silently
  }
};

export const trackCTAClick = (
  section: string,
  buttonText: string,
  destination: string
): void => {
  trackEvent('CTA Click', { section, buttonText, destination });
};

export const trackPlanSelection = (
  plan: 'base' | 'pro' | 'custom',
  period: 'monthly' | 'yearly'
): void => {
  trackEvent('Plan Selection', { plan, period });
};

export const trackPricingToggle = (period: 'monthly' | 'yearly'): void => {
  trackEvent('Pricing Toggle', { period });
};

export const trackFAQOpen = (question: string): void => {
  trackEvent('FAQ Open', { question });
};

export const trackScrollDepth = (percentage: number): void => {
  trackEvent('Scroll Depth', { percentage });
};

export const trackExternalLink = (destination: string, section: string): void => {
  trackEvent('External Link Click', { destination, section });
};

export function trackDashboardEvent(
  eventName: string,
  eventData?: Record<string, string | number | boolean | null>
): void {
  trackEvent(eventName, eventData);
}

export default trackEvent;